import {
  faComment,
  faEnvelope,
  faMessage,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {
  faLinkedin,
  faFacebook,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import HeroSection from "../../Components/Common/HeroSection";
import Button from "../../Components/Common/Button";
import { motion } from "framer-motion";
import useSWRMutation from "swr/mutation";
import { UserApiUrls } from "../../Api/Auth/ApiUrls";
import { sendPostRequest } from "../../Api/swrConfig";
import { useContact } from "../../Context/ContactContext";

const FALLBACK_BACKGROUND =
  "https://storage.googleapis.com/road-to-fe/uploads/1741417535659-07d447cd-82a2-4b1b-8c08-9fffeaaefa18-colleagues-doing-team-work-project_23-2149361607.avif";
const FALLBACK_DESCRIPTION =
  "If you have any questions or enquiries please feel free to reach out using the form or the social links below.";

const ContactUs = () => {
  const [captchaValue, setCaptchaValue] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    subject: "",
    email: "",
    enquiry: "",
  });
  const [status, setStatus] = useState("");

  const { contact, loading } = useContact();

  const heroTitle = contact?.heroTitle || "Contact Us";
  const heroSubtitle = contact?.heroSubtitle || "Find Us";
  const backgroundImage = contact?.backgroundImage || FALLBACK_BACKGROUND;
  const bodyHtml = contact?.bodyContent || `<p>${FALLBACK_DESCRIPTION}</p>`;
  const socialLinks = contact?.socialLinks || {};

  const contactEmail = socialLinks?.email || "support@roadtofe.com";
  const contactPhone = socialLinks?.phone || "";
  const contactAddress = socialLinks?.address || "";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const { trigger } = useSWRMutation(UserApiUrls.contact, sendPostRequest, {
    onSuccess: () => {
      toast.info(`We have recieved your message and will get back to you soon`);
    },
    onError: (error) => {
      toast.error(error.message || "Please try again later.");
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaValue) {
      toast.error("Please complete the reCAPTCHA");
      return;
    }
    setStatus("Sending...");
    try {
      await trigger(formData);
      setStatus("✅ Message sent successfully!");
      setFormData({
        fullName: "",
        subject: "",
        email: "",
        enquiry: "",
      });
      setCaptchaValue(null);
    } catch (err) {
      setStatus("❌ Failed to send message.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 1, y: -40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      <HeroSection
        title={heroTitle}
        backgroundImage={backgroundImage}
        subtitle={heroSubtitle}
      />
      <div className="container mx-auto pt-12">
        <div className="flex flex-col items-center md:flex-row items-start gap-12">
          <div className="md:w-1/2 text-center md:text-left">
            <div
              className="text-gray-600 leading-relaxed mb-4 space-y-4"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />

            <div className="flex flex-col gap-4 mt-6">
              {contactEmail && (
                <span>
                  <p className="font-semibold">Email</p>
                  <p>
                    <a
                      href={`mailto:${contactEmail}`}
                      className="text-blue-400"
                    >
                      {contactEmail}
                    </a>
                  </p>
                </span>
              )}
              {contactPhone && (
                <span>
                  <p className="font-semibold">Phone</p>
                  <p>{contactPhone}</p>
                </span>
              )}
              {contactAddress && (
                <span>
                  <p className="font-semibold">Address</p>
                  <p>{contactAddress}</p>
                </span>
              )}
            </div>

            {(socialLinks?.linkedin ||
              socialLinks?.facebook ||
              socialLinks?.youtube) && (
              <div className="flex items-center justify-center md:justify-start gap-4 mt-6">
                {socialLinks?.linkedin && (
                  <a
                    href={socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0a66c2] hover:text-[#084d94] text-2xl"
                    aria-label="LinkedIn"
                  >
                    <FontAwesomeIcon icon={faLinkedin} />
                  </a>
                )}
                {socialLinks?.facebook && (
                  <a
                    href={socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1877f2] hover:text-[#0f5dc1] text-2xl"
                    aria-label="Facebook"
                  >
                    <FontAwesomeIcon icon={faFacebook} />
                  </a>
                )}
                {socialLinks?.youtube && (
                  <a
                    href={socialLinks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#ff0000] hover:text-[#cc0000] text-2xl"
                    aria-label="YouTube"
                  >
                    <FontAwesomeIcon icon={faYoutube} />
                  </a>
                )}
              </div>
            )}
          </div>
          <div className="md:w-1/2 w-full text-center md:text-left">
            <form
              onSubmit={handleSubmit}
              className="w-full max-w-xxl bg-primary p-6 rounded-lg"
            >
              <h2 className="text-2xl font-bold text-center mb-4 text-white">
                Online Enquiry
              </h2>

              <div className="relative mb-4">
                <FontAwesomeIcon
                  icon={faUser}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white"
                />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Full Name*"
                  className="w-full p-3.5 pl-12 bg-[#ffffff1a] text-white border border-gray-600 rounded"
                  required
                />
              </div>

              <div className="relative mb-4">
                <FontAwesomeIcon
                  icon={faMessage}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white"
                />
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Subject*"
                  className="w-full p-3.5 pl-12 bg-[#ffffff1a] text-white border border-gray-600 rounded"
                  required
                />
              </div>

              <div className="relative mb-4">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white"
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email*"
                  className="w-full p-3.5 pl-12 bg-[#ffffff1a] text-white border border-gray-600 rounded"
                  required
                />
              </div>

              <div className="relative mb-4">
                <FontAwesomeIcon
                  icon={faComment}
                  className="absolute left-3 top-1/3 transform -translate-y-1/2 text-white"
                />
                <textarea
                  name="enquiry"
                  value={formData.enquiry}
                  onChange={handleChange}
                  placeholder="Enquiry"
                  className="w-full p-3.5 pl-12 bg-[#ffffff1a] text-white border border-gray-600 rounded"
                ></textarea>
              </div>

              <ReCAPTCHA
                id="recaptcha"
                sitekey="6LdHXjUrAAAAAOe_NncNDq0ZWx4faFPZaLOG3XBA"
                onChange={setCaptchaValue}
                style={{ height: "100px" }}
              />

              <Button type="secondary">Send</Button>
              {status && <p className="mt-2 text-white">{status}</p>}
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ContactUs;
